import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { usersApi, usersQueryKeys } from '../api';

// eslint_disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;

interface UseUserFormMutationsOptions {
  id?: number | string;
  onCreateSuccess?: () => void;
  onUpdateSuccess?: () => void;
}

export function useUserFormMutations({
  id,
  onCreateSuccess,
  onUpdateSuccess,
}: UseUserFormMutationsOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: fetchUser } = useMutation({
    mutationFn: (userId: number | string) => usersApi.getById(userId),
  });

  const { mutate: createUser, isPending: isCreating } = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      toast.success(t('createdSuccess', { entity: t('user') }));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
      onCreateSuccess?.();
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error);
      if (message?.includes('phone')) {
        toast.error(t('phoneAlreadyExists'));
      } else {
        toast.error(message || t('createError', { error: 'Unknown error' }));
      }
    },
  });

  const { mutate: updateUser, isPending: isUpdating } = useMutation({
    mutationFn: ({ userId, data }: { userId: number | string; data: Partial<FormValues> }) =>
      usersApi.update({ id: userId, data }),
    onSuccess: () => {
      toast.success(t('updatedSuccess', { entity: t('user') }));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
      onUpdateSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });

  return {
    fetchUser,
    createUser,
    updateUser,
    isPending: isCreating || isUpdating,
  };
}
